import { useEffect, useRef, useState, type FC, type MouseEvent } from "react";
import { motion } from "framer-motion";

interface Ripple {
  id: string;
  x: number;
  y: number;
  size: number;
}

interface Particle {
  id: string;
  x: number;
  y: number;
  size: number;
  duration: number;
  delay: number;
}

interface Burst {
  id: string;
  originX: number;
  originY: number;
  particles: Particle[];
}

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  target?: "_blank" | "_self";
  color?: "pink" | "gold" | "purple" | "rose";
  size?: "sm" | "md" | "lg";
  className?: string;
}

export const Button: FC<ButtonProps> = ({
  children,
  className,
  onClick,
  href,
  target = "_self",
  color = "gold",
  size = "md",
}) => {
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const [bursts, setBursts] = useState<Burst[]>([]);
  const timeoutIdsRef = useRef<number[]>([]);

  useEffect(
    () => () => {
      timeoutIdsRef.current.forEach(clearTimeout);
    },
    [],
  );

  const colors = {
    pink: "bg-pink-600 hover:bg-pink-700 active:bg-pink-800",
    gold: "bg-[var(--color-olive-gold)] hover:bg-[var(--color-olive-gold-hover)] active:bg-[var(--color-olive-gold-active)]",
    purple: "bg-purple-600 hover:bg-purple-700 active:bg-purple-800",
    rose: "bg-rose-600 hover:bg-rose-700 active:bg-rose-800",
  };

  const sizes = {
    sm: "px-6 py-2.5 text-sm",
    md: "px-8 py-3.5 text-base",
    lg: "px-10 py-4 text-lg",
  };

  const registerTimeout = (cb: () => void, delay: number) => {
    const id = window.setTimeout(cb, delay);
    timeoutIdsRef.current.push(id);
  };

  const handleClick = (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const rippleSize =
      Math.hypot(Math.max(x, rect.width - x), Math.max(y, rect.height - y)) * 2;

    const effectId = crypto.randomUUID();

    setRipples((prev) => [...prev, { id: effectId, x, y, size: rippleSize }]);

    const particles = Array.from({ length: 12 }, (_, i) => {
      const angle = (Math.PI * 2 * i) / 12 - Math.PI / 2;
      const distance = 0.6 + Math.random() * 0.4;

      return {
        id: `${effectId}-${i}`,
        x: Math.cos(angle) * 80 * distance,
        y: Math.sin(angle) * 60 * distance,
        size: 4 + Math.random() * 4,
        duration: 0.4 + Math.random() * 0.2,
        delay: i * 0.02,
      };
    });

    setBursts((prev) => [
      ...prev,
      { id: effectId, originX: x, originY: y, particles },
    ]);

    registerTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== effectId));
    }, 600);

    registerTimeout(() => {
      setBursts((prev) => prev.filter((b) => b.id !== effectId));
    }, 800);

    onClick?.();
  };

  const baseClass = `
    relative isolate inline-flex items-center justify-center overflow-hidden
    rounded-2xl font-medium text-white shadow-lg
    transition-all duration-200
    ${colors[color]} ${sizes[size]} ${className}
  `;

  const content = (
    <>
      {ripples.map((r) => (
        <motion.span
          key={r.id}
          className="pointer-events-none absolute rounded-full bg-white/30"
          initial={{ scale: 0, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 0 }}
          transition={{ duration: 0.6 }}
          style={{
            left: r.x,
            top: r.y,
            width: r.size,
            height: r.size,
            translate: "-50% -50%",
          }}
        />
      ))}

      {bursts.map((b) => (
        <div key={b.id} className="pointer-events-none absolute inset-0">
          {b.particles.map((p) => (
            <motion.div
              key={p.id}
              className="absolute rounded-full bg-white"
              initial={{ opacity: 1, scale: 0, x: 0, y: 0 }}
              animate={{
                opacity: [1, 0],
                scale: [0, 1.2],
                x: p.x,
                y: p.y,
              }}
              transition={{
                duration: p.duration,
                delay: p.delay,
              }}
              style={{
                left: b.originX,
                top: b.originY,
                width: p.size,
                height: p.size,
              }}
            />
          ))}
        </div>
      ))}

      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </>
  );

  // 🔥 Вот ключевая часть
  if (href) {
    return (
      <motion.a
        href={href}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
        whileHover={{ scale: 1.08, y: -2 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleClick}
        className={baseClass}
      >
        {content}
      </motion.a>
    );
  }

  return (
    <motion.button
      whileHover={{ scale: 1.08, y: -2 }}
      whileTap={{ scale: 0.95 }}
      onClick={handleClick}
      className={baseClass}
    >
      {content}
    </motion.button>
  );
};
